import { Checkbox } from '@apps/registry/common/components/RegistryChecklist/components';
import { VIPData } from '@shared/components/VIPEditor/components/VIPForm';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { Trash } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import React from 'react';
import { SlideWrapper } from './SlideWrapper';
import { ReorderArrow } from './ReorderArrow';

const DeleteButton = (props: { onClick: () => void; visible: boolean }) => {
  const { onClick, visible } = props;

  return (
    <Flex
      as="button"
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      opacity={visible ? 1 : 0}
      filter={visible ? 'blur(0px)' : 'blur(8px)'}
      transition="opacity 0.2s ease, filter 0.3s cubic-bezier(0.4,0,0.2,1)"
      className="delete"
      position="absolute"
      border="2px solid"
      borderColor="mono3"
      right={pxToRem(28)}
      top="50%"
      transform="translateY(-50%)"
      borderRadius={pxToRem(20)}
      width={pxToRem(40)}
      height={pxToRem(40)}
      alignItems="center"
      justifyContent="center"
      backgroundColor="white"
      color="mono14"
      _hover={{ color: 'negative5' }}
      _active={{ color: 'negative7' }}
    >
      <Trash size="sm" />
    </Flex>
  );
};

const VipImage = (props: { imageUrl?: string | null }) => {
  const { imageUrl } = props;
  return (
    <Box
      height="50px"
      width="50px"
      marginRight="1rem"
      flexShrink={0}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="VIP"
          style={{ height: '50px', width: '50px', borderRadius: '5px', objectFit: 'cover' }}
        />
      ) : (
        <Box
          height="50px"
          width="50px"
          borderRadius="5px"
          backgroundColor="mono3"
        />
      )}
    </Box>
  );
};

type VipCardProps = {
  data: VIPData;
  onClick: () => void;
  onDeleteClick: () => void;
  showFilterButton?: boolean;
  isExcluded?: boolean;
  onFilterClick?: (excluded: boolean) => void;
  onReorder?: (direction: 'up' | 'down') => void;
};

export const VipCard = (props: VipCardProps) => {
  const { data, onClick, onDeleteClick, isExcluded, showFilterButton, onFilterClick, onReorder } = props;
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <Box
      padding="1rem"
      border="1px solid"
      borderColor="mono4"
      borderRadius="0.5rem"
      cursor="pointer"
      position="relative"
      userSelect="none"
      transition="background-color 0.2s ease"
      _hover={{ backgroundColor: 'mono2' }}
      onClick={() => {
        if (!showFilterButton) {
          onClick();
        } else {
          // The whole card should toggle filter state when in filter mode
          onFilterClick?.(!isExcluded);
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Flex>
        <SlideWrapper show={showFilterButton ?? false}>
          <Checkbox
            isChecked={!isExcluded}
            onClick={e => {
              // Use click event to prevent card onClick from firing
              e.stopPropagation();
            }}
            onChange={e => {
              // Using onChange to trigger event.
              // onClick was firing twice...
              onFilterClick?.(!isExcluded);
            }}
            id="filter-checkbox"
            margin="auto 1rem auto 0"
          />
        </SlideWrapper>
        <VipImage imageUrl={data.photoUrl} />
        <Flex flexDirection={'column'}>
          <TextV2 typographyVariant="hed2">{data.alias}</TextV2>
          <TextV2 typographyVariant="body1">{data.title}</TextV2>
        </Flex>
        {!showFilterButton && (
          <DeleteButton
            onClick={onDeleteClick}
            visible={isHovered}
          />
        )}
        {showFilterButton && !isExcluded && (
          <ReorderArrow
            onUp={() => onReorder?.('up')}
            onDown={() => onReorder?.('down')}
          />
        )}
      </Flex>
    </Box>
  );
};
